// Brand shortcuts keep the existing catalogue routing/refinement behaviour.
// A one-shot intent only scrolls after the connected results grid is ready.
const INTENT_KEY = 'raffi:brand-results-scroll:v1';
const REQUEST_EVENT = 'native-brand-results-scroll-requested';
const READY_EVENT = 'native-brand-results-ready';
const MAX_INTENT_AGE = 120000;
const PRIVATE_HOST = 'raffi-internal-mod-dev-staging-f924c54ebe58.herokuapp.com';
const PUBLIC_HOSTS = ['www.raffi-jewellers.ca', 'raffi-jewellers.ca'];
const ACTIVE_HOSTS = [PRIVATE_HOST, ...PUBLIC_HOSTS];
const protectedWatch = (value: string): boolean => {
  let text = value;
  try { text = decodeURIComponent(text); } catch { /* Malformed values remain excluded by path validation. */ }
  return /rolex|tag[\s_-]*heuer/i.test(text);
};
const collectionPath = (path: string): boolean => /^\/(?:fr\/)?collections\/timepieces(?:\/[a-z0-9-]+)*\/?$/i.test(path)
  && !protectedWatch(path);

const enabledRoot = (): HTMLElement | null => {
  if (!ACTIVE_HOSTS.includes(window.location.hostname) || !collectionPath(window.location.pathname)) return null;
  return document.querySelector<HTMLElement>('main[data-brand-results-scroll]');
};

const destinationKey = (url: URL): string => {
  const entries = Array.from(url.searchParams.entries()).sort(([a, av], [b, bv]) => a.localeCompare(b) || av.localeCompare(bv));
  return url.pathname.replace(/\/$/, '') + '?' + new URLSearchParams(entries).toString();
};

/** Eligible shortcuts keep the current public origin or authenticated private preview. */
export const rememberCollectionResultsDestination = (href: string): string | undefined => {
  if (!enabledRoot()) return undefined;
  let destination: URL;
  try {
    destination = new URL(href, window.location.href);
    const destinationHosts = window.location.hostname === PRIVATE_HOST ? ACTIVE_HOSTS : PUBLIC_HOSTS;
    if (destination.protocol !== 'https:' || !destinationHosts.includes(destination.hostname) || destination.port
      || destination.username || destination.password || destination.hash || !collectionPath(destination.pathname)
      || Array.from(destination.searchParams.entries()).some(([name, value]) => /^(?:key|inquiry|modal|form)$/i.test(name)
        || (/brand|vendor/i.test(name) && protectedWatch(value)))) return undefined;
    const locale = /^\/fr(?:\/|$)/i.test(window.location.pathname) ? 'fr' : 'en';
    destination.hostname = window.location.hostname;
    destination.pathname = (locale === 'fr' ? '/fr' : '') + destination.pathname.replace(/^\/fr(?=\/)/i, '');
    destination.searchParams.set('lang', locale);
  } catch { return undefined; }
  try {
    sessionStorage.setItem(INTENT_KEY, JSON.stringify({ destination: destinationKey(destination), at: Date.now() }));
  } catch { /* Storage restrictions must never prevent safe local navigation. */ }
  return destination.href;
};

export const beginCollectionResults = (container: HTMLElement): number | undefined => {
  const root = enabledRoot();
  if (!root || !container.isConnected || container.id !== 'hits') return undefined;
  const revision = Number(root.dataset.resultsRevision || 0) + 1;
  root.dataset.resultsRevision = String(revision);
  root.dataset.resultsReady = 'false';
  return revision;
};

export const completeCollectionResults = (container: HTMLElement, revision?: number): void => {
  const root = enabledRoot();
  if (!root || revision === undefined || !container.isConnected || container.id !== 'hits'
    || Number(root.dataset.resultsRevision) !== revision) return;
  root.dataset.resultsReady = 'true';
  document.dispatchEvent(new CustomEvent(READY_EVENT, { detail: { revision } }));
};

// Returns false outside an eligible watch catalogue so legacy price
// controls keep their existing behaviour on all other pages.
export const requestCollectionResultsScroll = (waitForUpdate: boolean): boolean => {
  if (!enabledRoot()) return false;
  document.dispatchEvent(new CustomEvent(REQUEST_EVENT, { detail: { waitForUpdate } }));
  return true;
};

const initialize = (): void => {
  const root = enabledRoot();
  if (!root || root.dataset.resultsScrollInitialized === 'true') return;
  root.dataset.resultsScrollInitialized = 'true';
  let pendingRevision: number | null = null;
  let correctionTimer = 0;
  let fallbackTimer = 0;
  let scrollEnd: (() => void) | null = null;

  const cancel = (): void => {
    pendingRevision = null;
    window.clearTimeout(correctionTimer);
    window.clearTimeout(fallbackTimer);
    if (scrollEnd) window.removeEventListener('scrollend', scrollEnd);
    scrollEnd = null;
  };

  const scrollToResults = (): void => {
    const target = document.getElementById('collectionWrapper');
    if (!target) return;
    cancel();
    const top = (): number => {
      const header = Math.max(0, parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--nav-sticky-offset')) || 0);
      const brandBars = Array.from(document.querySelectorAll<HTMLElement>('.js-brand-subnav'));
      const brandHeight = brandBars.reduce((height, bar) => bar.getClientRects().length ? Math.max(height, bar.getBoundingClientRect().height) : height, 0);
      return Math.max(0, window.scrollY + target.getBoundingClientRect().top - header - brandHeight - 12);
    };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const correct = (): void => {
      window.clearTimeout(fallbackTimer);
      if (scrollEnd) window.removeEventListener('scrollend', scrollEnd);
      scrollEnd = null;
      // The main nav hides/compresses while scrolling. Read its final offset.
      correctionTimer = window.setTimeout(() => {
        if (Math.abs(window.scrollY - top()) > 4) window.scrollTo({ top: top(), behavior: 'auto' });
      }, 350);
    };
    scrollEnd = correct;
    window.addEventListener('scrollend', scrollEnd, { once: true });
    fallbackTimer = window.setTimeout(correct, reducedMotion ? 0 : 1600);
    window.scrollTo({ top: top(), behavior: reducedMotion ? 'auto' : 'smooth' });
  };

  const consumeReady = (): void => {
    if (pendingRevision !== null && root.dataset.resultsReady === 'true'
      && Number(root.dataset.resultsRevision || 0) >= pendingRevision) scrollToResults();
  };

  document.addEventListener(REQUEST_EVENT, ((event: CustomEvent<{ waitForUpdate: boolean }>) => {
    cancel();
    pendingRevision = Number(root.dataset.resultsRevision || 0) + (event.detail.waitForUpdate ? 1 : 0);
    consumeReady();
  }) as EventListener);
  document.addEventListener(READY_EVENT, consumeReady);

  // Do not pull the shopper back after they choose to scroll or interact while
  // a slow filter response or nav animation is still in flight.
  ['wheel', 'touchstart', 'pointerdown'].forEach(type => window.addEventListener(type, cancel, { passive: true }));
  window.addEventListener('keydown', event => {
    if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', 'Escape', 'Tab'].includes(event.key)) cancel();
  });

  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = (event.target as Element).closest<HTMLAnchorElement>('a[href]');
    if (!link || !root.contains(link) || link.hasAttribute('download') || (link.target && link.target !== '_self')
      || !link.closest('[data-watch-brand-cards], .fc-brand-directory, .fc-collection-gallery, .slider-cc, .buttons-module--price-nav .price-filter__pill, .js-btn-tier-link')) return;
    const destination = rememberCollectionResultsDestination(link.href);
    if (!destination) return;
    event.preventDefault();
    window.location.assign(destination);
  });

  try {
    const saved = sessionStorage.getItem(INTENT_KEY);
    sessionStorage.removeItem(INTENT_KEY);
    const intent = saved ? JSON.parse(saved) : null;
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (intent && navigation?.type !== 'back_forward' && typeof intent.at === 'number'
      && Date.now() >= intent.at && Date.now() - intent.at < MAX_INTENT_AGE
      && intent.destination === destinationKey(new URL(window.location.href))) {
      pendingRevision = Math.max(1, Number(root.dataset.resultsRevision || 0));
      consumeReady();
    }
  } catch { /* Malformed/expired intent is safely ignored. */ }
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
else initialize();
